import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useServices } from '../../../services/useServices';
import { IAuthorRelationshipResponseDto } from '../book.dto';

// The backend reads HubSpot live on every call and keeps no cache, so freshness is
// owned here: reuse a read for 5 minutes, and "Refresh" overwrites it on demand.
export const AUTHOR_RELATIONSHIP_STALE_MS = 5 * 60 * 1000;

export const authorRelationshipKey = (author: string) => ['author-relationship', author];

export const useAuthorRelationship = (author: string) => {
  const {
    book: { getAuthorRelationship }
  } = useServices();
  const queryClient = useQueryClient();
  const queryKey = authorRelationshipKey(author);

  const res = useQuery<IAuthorRelationshipResponseDto>(
    queryKey,
    () => getAuthorRelationship(author),
    {
      enabled: Boolean(author),
      staleTime: AUTHOR_RELATIONSHIP_STALE_MS,
      cacheTime: AUTHOR_RELATIONSHIP_STALE_MS * 2,
      refetchOnWindowFocus: false,
      retry: 1,
      retryDelay: 500
    }
  );

  const refresh = useMutation<IAuthorRelationshipResponseDto>(
    () => getAuthorRelationship(author, true),
    {
      onSuccess: (fresh) => {
        queryClient.setQueryData(queryKey, fresh);
      }
    }
  );

  return {
    data: res.data,
    isError: res.isError,
    isLoading: res.isLoading,
    refetch: res.refetch,
    refresh: refresh.mutate,
    isRefreshing: refresh.isLoading,
    isRefreshError: refresh.isError
  };
};
